import { Button } from '@/components/ui/button'
import { DateInput } from '@/components/ui/date-input'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DATE_PRESETS,
  monthRange,
  presetRange,
  wholeMonthOf,
  wholeYearOf,
  yearRange,
} from '@/features/records/utils/recordFilters'

interface DateRangePickerProps {
  /** `YYYY-MM-DD` in local time. */
  from?: string
  to?: string
  onChange: (from?: string, to?: string) => void
  /** Prefix for the field ids, so two pickers on one page do not share labels. */
  idPrefix?: string
}

const YEAR_COUNT = 10

/**
 * Panel for choosing a day range: quick presets, a month, a year or custom dates. Presets resolve
 * to concrete dates, so the result is the same plain `from`/`to` pair a custom range gives (and
 * links that carry it keep meaning the same days). Meant to sit inside a popover.
 */
export function DateRangePicker({ from, to, onChange, idPrefix = 'range' }: DateRangePickerProps) {
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: YEAR_COUNT }, (_, i) => currentYear - i)
  const month = wholeMonthOf(from, to) ?? ''
  const year = wholeYearOf(from, to)
  const hasRange = !!from || !!to

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-1.5" role="group" aria-label="Quick ranges">
        {DATE_PRESETS.map((preset) => (
          <Button
            key={preset.id}
            type="button"
            variant="outline"
            size="sm"
            className="h-8 justify-start text-xs"
            onClick={() => {
              const range = presetRange(preset.id)
              onChange(range.from, range.to)
            }}
          >
            {preset.label}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Label htmlFor={`${idPrefix}-month`} className="space-y-1 text-xs text-muted-foreground">
          Month
          <Input
            id={`${idPrefix}-month`}
            type="month"
            className="h-9"
            value={month}
            onChange={(e) => {
              const range = e.target.value ? monthRange(e.target.value) : null
              if (range) onChange(range.from, range.to)
            }}
          />
        </Label>
        <div className="space-y-1 text-xs text-muted-foreground">
          <span id={`${idPrefix}-year-label`}>Year</span>
          <Select
            value={year !== null && years.includes(year) ? String(year) : ''}
            onValueChange={(value) => {
              const range = yearRange(Number(value))
              onChange(range.from, range.to)
            }}
          >
            <SelectTrigger className="h-9" aria-labelledby={`${idPrefix}-year-label`}>
              <SelectValue placeholder="Choose…" />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Label htmlFor={`${idPrefix}-from`} className="space-y-1 text-xs text-muted-foreground">
          From
          <DateInput
            id={`${idPrefix}-from`}
            value={from ?? ''}
            max={to || undefined}
            onChange={(v) => onChange(v || undefined, to)}
          />
        </Label>
        <Label htmlFor={`${idPrefix}-to`} className="space-y-1 text-xs text-muted-foreground">
          To
          <DateInput
            id={`${idPrefix}-to`}
            value={to ?? ''}
            min={from || undefined}
            onChange={(v) => onChange(from, v || undefined)}
          />
        </Label>
      </div>

      {hasRange && (
        <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onChange(undefined, undefined)}>
          Clear
        </Button>
      )}
    </div>
  )
}
